import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getLesson, getLessonBlocks, checkContentEntitlement,
  upsertProgress, toggleBookmark, askAcademyGuide, saveNote, listNotesForLesson,
  submitLessonAssessment, recordScenarioMastery, getLessonMastery,
} from "@/lib/academy.functions";
import { LessonBlocks, type LessonBlock } from "@/academy/blocks/LessonBlocks";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { Award, Bookmark, Check, Lock, MessageCircle, NotebookPen, Send } from "lucide-react";
import { toast } from "sonner";
import {
  NOINDEX_META, breadcrumbs, isoMinutes, ldScript, learningResourceNode, seoMeta,
} from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";
import { booksForTopics, topicLabel } from "@/lib/topics";
import { TopicChips } from "@/components/site/TopicChips";

export const Route = createFileRoute("/academy/$domain/lesson/$lesson")({
  loader: async ({ params }) => {
    const lesson = await getLesson({ data: { domainSlug: params.domain, lessonSlug: params.lesson } });
    if (!lesson) throw notFound();
    return lesson;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Lesson unavailable — Buddha KA Academy" }, NOINDEX_META] };
    }
    const l = loaderData;
    const path = `/academy/${params.domain}/lesson/${params.lesson}`;
    const title = `${l.title} — Buddha KA Academy`;
    const description =
      l.summary ?? l.subtitle ?? `${l.title}: a university-depth lesson from the Buddha KA Academy.`;
    return {
      ...seoMeta({
        title,
        description,
        path,
        ogType: "article",
        ...(l.hero_image_url ? { image: l.hero_image_url, imageAlt: l.title } : {}),
      }),
      scripts: [
        ldScript(
          learningResourceNode({
            id: `${absoluteUrl(path)}#lesson`,
            url: absoluteUrl(path),
            name: l.title,
            description,
            partOfCourseId: `${absoluteUrl(`/academy/${params.domain}`)}#course`,
            educationalLevel: l.difficulty ?? undefined,
            timeRequired: isoMinutes(l.est_minutes),
            keywords: l.tags ?? undefined,
            learningObjectives: l.learning_objectives ?? undefined,
            wordCount: l.word_count ?? undefined,
            datePublished: l.published_at ?? undefined,
            dateModified: l.updated_at ?? undefined,
            image: l.hero_image_url ?? undefined,
            extra: (() => {
              const books = booksForTopics(l.tags, { limit: 3 });
              return books.length
                ? {
                    citation: books.map((m) => ({
                      "@type": "Book",
                      name: m.book.title,
                      url: absoluteUrl(`/books/${m.book.slug}`),
                    })),
                    relatedLink: books.map((m) => absoluteUrl(`/books/${m.book.slug}`)),
                  }
                : {};
            })(),
          }),
        ),
        ldScript(
          breadcrumbs([
            ["Academy", "/academy"],
            [params.domain, `/academy/${params.domain}`],
            [l.title, path],
          ]),
        ),
      ],
    };
  },

  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Lesson not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-foreground/60">{(error as Error).message}</p>
    </div>
  ),
  component: LessonPage,
});

function LessonPage() {
  const lesson = Route.useLoaderData();
  const { domain } = Route.useParams();
  const [authed, setAuthed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [note, setNote] = useState("");
  const [noteId, setNoteId] = useState<string | undefined>();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const complete = useServerFn(upsertProgress);
  const bmk = useServerFn(toggleBookmark);
  const ask = useServerFn(askAcademyGuide);
  const persistNote = useServerFn(saveNote);
  const loadNotes = useServerFn(listNotesForLesson);

  useEffect(() => {
    track("lesson_start", { lesson: lesson.slug, domain });
    supabase.auth.getUser().then(async ({ data }) => {
      const isAuthed = Boolean(data.user);
      setAuthed(isAuthed);
      if (isAuthed) {
        try {
          const notes = await loadNotes({ data: { lessonId: lesson.id } });
          if (notes[0]) { setNote(notes[0].body_md); setNoteId(notes[0].id); }
        } catch { /* ignore */ }
      }
    });
  }, [lesson.id, loadNotes]);

  async function markComplete() {
    if (!authed) { toast.error("Sign in to track progress"); return; }
    await complete({ data: { lessonId: lesson.id, progressPct: 100, status: "completed" } });
    setCompleted(true);
    track("lesson_complete", { lesson: lesson.slug, domain });
    toast.success("Lesson complete +10 XP");
  }

  async function saveMyNote() {
    if (!authed) { toast.error("Sign in to save notes"); return; }
    if (!note.trim()) return;
    const r = await persistNote({ data: { lessonId: lesson.id, body: note, id: noteId } });
    setNoteId(r.id);
    toast.success("Note saved");
  }

  async function submitAsk(e: React.FormEvent) {
    e.preventDefault();
    if (!authed) { toast.error("Sign in to use the Guide"); return; }
    if (!question.trim()) return;
    setAsking(true);
    try {
      const r = await ask({ data: { lessonId: lesson.id, question } });
      setAnswer(r.answer);
    } catch (e) { toast.error((e as Error).message); }
    finally { setAsking(false); }
  }

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <Link to="/academy/$domain" params={{ domain }} className="text-xs uppercase tracking-[0.3em] text-gold">
          ← {domain.replace(/-/g, " ")}
        </Link>
        <h1 className="mt-3 font-serif text-4xl text-gold-luminous">{lesson.title}</h1>
        {lesson.subtitle && <p className="mt-1 text-sm uppercase tracking-widest text-foreground/60">{lesson.subtitle}</p>}

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={markComplete}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${completed ? "bg-emerald-500/20 text-emerald-300" : "bg-gold-gradient text-obsidian font-semibold"}`}>
            <Check size={14} /> {completed ? "Completed" : "Mark complete"}
          </button>
          <button onClick={async () => { const r = await bmk({ data: { lessonId: lesson.id } }); setBookmarked(r.bookmarked); }}
            className={`inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-sm ${bookmarked ? "text-gold border-gold" : ""}`}>
            <Bookmark size={14} /> {bookmarked ? "Saved" : "Save"}
          </button>
        </div>

        <LessonObjectives objectives={lesson.learning_objectives ?? []} minutes={lesson.est_minutes} words={lesson.word_count} />

        <LessonSections
          sections={lesson.sections as LessonSection[] | null}
          fallbackBody={lesson.body_md}
          provenance={lesson.provenance as Record<string, string> | null}
        />

        <LessonContexts
          scientific={lesson.scientific_context}
          historical={lesson.historical_context}
          traditional={lesson.traditional_context}
          kafOriginal={lesson.kaf_original}
        />

        <LessonScenarios applications={lesson.applications as unknown} lessonId={lesson.id} authed={authed} />

        <LessonBlocksSection lessonId={lesson.id} domainId={lesson.domain_id} pathId={null} authed={authed} />

        <LessonAssessment
          lessonId={lesson.id}
          domain={domain}
          quiz={lesson.quiz}
          prompts={lesson.prompts}
          nextLessonSlugs={(lesson.next_lesson_slugs as string[] | null) ?? []}
          authed={authed}
          onCompleted={() => setCompleted(true)}
        />

        <TopicChips tags={lesson.tags} text={[lesson.title, lesson.summary].filter(Boolean).join(" ")} className="mb-8" />
        <RelatedBooks tags={lesson.tags} />





        {authed && (
          <>
            <section className="mt-12 rounded-2xl border hairline p-5">
              <h2 className="flex items-center gap-2 font-serif text-xl text-gold-luminous">
                <NotebookPen size={18} /> Your notes
              </h2>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Capture insights as you study…"
                className="mt-3 min-h-[140px] w-full rounded-md border hairline bg-obsidian/60 p-3 text-sm" />
              <button onClick={saveMyNote} className="mt-3 rounded-full border hairline px-4 py-2 text-sm">Save note</button>
            </section>

            <section className="mt-8 rounded-2xl border hairline p-5">
              <h2 className="flex items-center gap-2 font-serif text-xl text-gold-luminous">
                <MessageCircle size={18} /> Ask the Academy Guide
              </h2>
              <form onSubmit={submitAsk} className="mt-3 flex gap-2">
                <input value={question} onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask about this lesson…"
                  className="flex-1 rounded-md border hairline bg-obsidian/60 px-3 py-2 text-sm" />
                <button disabled={asking} className="inline-flex items-center gap-1 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian">
                  <Send size={14} /> {asking ? "…" : "Ask"}
                </button>
              </form>
              {answer && (
                <div className="mt-4 whitespace-pre-wrap rounded-md border hairline bg-obsidian/40 p-4 text-sm text-foreground/85">
                  {answer}
                </div>
              )}
            </section>
          </>
        )}
      </article>
    </div>
  );
}



type LessonSection = {
  heading?: string;
  title?: string;
  body_md?: string;
  body?: string;
  provenance?: string;
};

const PROVENANCE_STYLE: Record<string, string> = {
  evidence: "border-emerald-500/50 text-emerald-300",
  historical: "border-amber-500/50 text-amber-300",
  traditional: "border-violet-500/50 text-violet-300",
  kaf_original: "border-gold text-gold-luminous",
};
const PROVENANCE_LABEL: Record<string, string> = {
  evidence: "Evidence",
  historical: "Historical",
  traditional: "Traditional",
  kaf_original: "KAF Original",
};

function ProvenanceBadge({ kind }: { kind?: string | null }) {
  if (!kind) return null;
  const style = PROVENANCE_STYLE[kind] ?? "border-foreground/30 text-foreground/60";
  const label = PROVENANCE_LABEL[kind] ?? kind;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${style}`}>
      {label}
    </span>
  );
}

function LessonObjectives({ objectives, minutes, words }: {
  objectives: string[]; minutes: number; words: number;
}) {
  if (!objectives.length) return null;
  return (
    <section className="mt-8 rounded-2xl border hairline p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Learning objectives</h2>
        <p className="text-[10px] uppercase tracking-widest text-foreground/50">
          {minutes} min · {words.toLocaleString()} words
        </p>
      </div>
      <ul className="mt-3 space-y-2 text-sm text-foreground/85">
        {objectives.map((o, i) => (
          <li key={i} className="flex gap-2"><span className="text-gold">◆</span><span>{o}</span></li>
        ))}
      </ul>
    </section>
  );
}

function LessonSections({ sections, fallbackBody, provenance }: {
  sections: LessonSection[] | null;
  fallbackBody: string;
  provenance: Record<string, string> | null;
}) {
  const list = Array.isArray(sections) ? sections : [];
  if (!list.length) {
    return (
      <div className="prose prose-invert mt-8 max-w-none whitespace-pre-wrap font-serif text-base leading-relaxed">
        {fallbackBody}
      </div>
    );
  }
  return (
    <div className="mt-8 space-y-8">
      {list.map((s, i) => {
        const heading = s.heading ?? s.title ?? `Section ${i + 1}`;
        const body = s.body_md ?? s.body ?? "";
        const kind = s.provenance ?? provenance?.[String(i)] ?? provenance?.[heading];
        return (
          <section key={i} className="scroll-mt-24" id={`section-${i + 1}`}>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-serif text-2xl text-gold-luminous">{heading}</h2>
              <ProvenanceBadge kind={kind} />
            </div>
            <div className="prose prose-invert mt-3 max-w-none whitespace-pre-wrap font-serif text-base leading-relaxed">
              {body}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function LessonContexts({ scientific, historical, traditional, kafOriginal }: {
  scientific: string | null; historical: string | null; traditional: string | null; kafOriginal: string | null;
}) {
  const items = [
    { k: "evidence", label: "Scientific context", body: scientific },
    { k: "historical", label: "Historical context", body: historical },
    { k: "traditional", label: "Traditional context", body: traditional },
    { k: "kaf_original", label: "KAF original", body: kafOriginal },
  ].filter((i) => i.body && i.body.trim().length > 0);
  if (!items.length) return null;
  return (
    <section className="mt-10 grid gap-4 md:grid-cols-2">
      {items.map((i) => (
        <div key={i.k} className="rounded-2xl border hairline p-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground/85">{i.label}</h3>
            <ProvenanceBadge kind={i.k} />
          </div>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{i.body}</p>
        </div>
      ))}
    </section>
  );
}


// ---- Unified LessonAssessment (reflections + server-scored quiz) ----

type SeedQuizItem = { q: string; choices?: string[]; options?: string[]; answer: number; explanation?: string; explain?: string };
type PromptShape = string | { prompt?: string; text?: string };

function normalizeQuiz(quiz: unknown): SeedQuizItem[] {
  if (!quiz) return [];
  if (Array.isArray(quiz)) return quiz as SeedQuizItem[];
  const q = quiz as { questions?: SeedQuizItem[]; items?: SeedQuizItem[] };
  return (q.questions ?? q.items ?? []) as SeedQuizItem[];
}

function normalizePrompts(prompts: unknown): { reflection: string[]; practice: string | null } {
  if (!prompts) return { reflection: [], practice: null };
  if (Array.isArray(prompts)) {
    return {
      reflection: (prompts as PromptShape[])
        .map((p) => (typeof p === "string" ? p : p?.prompt ?? p?.text ?? ""))
        .filter(Boolean),
      practice: null,
    };
  }
  const o = prompts as { reflection?: (string | { prompt?: string })[]; practice?: string };
  return {
    reflection: (o.reflection ?? [])
      .map((p) => (typeof p === "string" ? p : p?.prompt ?? ""))
      .filter(Boolean),
    practice: o.practice ?? null,
  };
}

function LessonAssessment({
  lessonId, domain, quiz, prompts, nextLessonSlugs, authed, onCompleted,
}: {
  lessonId: string;
  domain: string;
  quiz: unknown;
  prompts: unknown;
  nextLessonSlugs: string[];
  authed: boolean;
  onCompleted: () => void;
}) {
  const items = normalizeQuiz(quiz);
  const { reflection, practice } = normalizePrompts(prompts);
  const submit = useServerFn(submitLessonAssessment);

  const [picks, setPicks] = useState<Record<number, number>>({});
  const [reflections, setReflections] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof submit>> | null>(null);

  if (!items.length && !reflection.length && !practice) return null;

  const allAnswered = items.length > 0 && items.every((_, i) => typeof picks[i] === "number");

  async function handleSubmit() {
    if (!authed) { toast.error("Sign in to record your assessment"); return; }
    if (!allAnswered) { toast.error("Answer every question first"); return; }
    setSubmitting(true);
    try {
      const answers = items.map((_, i) => picks[i]);
      const refArr = reflection.map((_, i) => reflections[i] ?? "");
      const r = await submit({ data: { lessonId, answers, reflections: refArr } });
      setResult(r);
      if (r.passed) {
        onCompleted();
        toast.success(`Passed · ${r.masteryPct}%  +25 XP`);
      } else {
        toast.message(`Score ${r.masteryPct}% — needs ${r.threshold}% to pass`);
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setSubmitting(false); }
  }

  function reset() { setPicks({}); setReflections({}); setResult(null); }

  return (
    <section className="mt-12 space-y-8">
      {/* Reflections */}
      {reflection.length > 0 && (
        <div className="rounded-2xl border hairline p-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-serif text-xl text-gold-luminous">Reflect before you answer</h3>
            <span className="text-[10px] uppercase tracking-widest text-foreground/50">Saved to your Journal</span>
          </div>
          <div className="mt-4 space-y-5">
            {reflection.map((p, i) => (
              <div key={i}>
                <p className="text-sm text-foreground/85">{i + 1}. {p}</p>
                <textarea
                  value={reflections[i] ?? ""}
                  onChange={(e) => setReflections({ ...reflections, [i]: e.target.value })}
                  disabled={Boolean(result)}
                  placeholder={authed ? "Your reflection…" : "Sign in to save your reflection"}
                  className="mt-2 min-h-[92px] w-full rounded-md border hairline bg-obsidian/60 p-3 text-sm disabled:opacity-70"
                />
              </div>
            ))}
          </div>
          {practice && (
            <div className="mt-5 rounded-lg border border-gold/30 bg-gold/5 p-3 text-sm text-foreground/85">
              <span className="text-xs uppercase tracking-widest text-gold">Practice · </span>{practice}
            </div>
          )}
        </div>
      )}

      {/* Quiz */}
      {items.length > 0 && (
        <div className="rounded-2xl border hairline p-5">
          <h3 className="font-serif text-xl text-gold-luminous">Knowledge check</h3>
          <p className="mt-1 text-xs text-foreground/60">Answer all {items.length} to submit. 70% or higher to complete this lesson.</p>
          <ol className="mt-5 space-y-6">
            {items.map((q, i) => {
              const opts = q.choices ?? q.options ?? [];
              const rItem = result?.results?.[i];
              return (
                <li key={i}>
                  <p className="text-sm font-medium">{i + 1}. {q.q}</p>
                  <ul className="mt-2 space-y-1">
                    {opts.map((opt, j) => {
                      const picked = picks[i] === j;
                      const isCorrect = result && j === q.answer;
                      const isWrongPick = result && picked && j !== q.answer;
                      return (
                        <li key={j}>
                          <button
                            type="button"
                            onClick={() => !result && setPicks({ ...picks, [i]: j })}
                            disabled={Boolean(result)}
                            className={`w-full min-h-11 rounded-lg border hairline px-3 py-2 text-left text-sm transition ${
                              isCorrect ? "border-emerald-500 bg-emerald-500/10"
                              : isWrongPick ? "border-red-500 bg-red-500/10"
                              : picked ? "border-gold" : ""
                            }`}
                          >
                            {opt}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {result && rItem && (q.explanation || q.explain) && (
                    <p className={`mt-2 text-xs ${rItem.correct ? "text-emerald-300" : "text-foreground/60"}`}>
                      {q.explanation ?? q.explain}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {!result ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className="rounded-full bg-gold-gradient px-5 py-2 text-sm font-semibold text-obsidian disabled:opacity-50"
              >
                {submitting ? "Submitting…" : authed ? "Submit assessment" : "Sign in to submit"}
              </button>
            ) : (
              <>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                    result.passed ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/15 text-amber-300"
                  }`}>
                    {result.passed ? "Passed" : "Keep going"} · {result.masteryPct}%
                  </span>
                  <span className="text-foreground/70">{result.correctCount}/{result.total} correct</span>
                </div>
                <button type="button" onClick={reset} className="rounded-full border hairline px-4 py-1.5 text-xs">
                  Retake
                </button>
                {result.passed && nextLessonSlugs.length > 0 && (
                  <Link
                    to="/academy/$domain/lesson/$lesson"
                    params={{ domain, lesson: nextLessonSlugs[0] }}
                    className="rounded-full bg-gold-gradient px-4 py-1.5 text-xs font-semibold text-obsidian"
                  >
                    Next lesson →
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


function LessonBlocksSection({ lessonId, domainId, pathId, authed }: {
  lessonId: string; domainId: string | null; pathId: string | null; authed: boolean;
}) {
  const { data: blocks } = useQuery({
    queryKey: ["academy", "lesson-blocks", lessonId],
    queryFn: () => getLessonBlocks({ data: { lessonId } }),
  });
  const { data: gate } = useQuery({
    queryKey: ["academy", "gate", lessonId, authed],
    queryFn: () => checkContentEntitlement({ data: { lessonId, domainId: domainId ?? undefined, pathId: pathId ?? undefined } }),
    enabled: authed,
  });
  if (!blocks?.length) return null;
  const locked = Boolean(gate?.locked);
  return (
    <section className="mt-10">
      {!authed && blocks.some((b) => b.entitlement_slug) && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-gold/40 bg-gold/5 p-3 text-sm text-gold">
          <Lock size={14} /> Sign in to unlock premium blocks.
        </div>
      )}
      <LessonBlocks blocks={blocks as LessonBlock[]} locked={locked} />
    </section>
  );
}

// ---- Interactive Scenarios (instant client-side feedback) ----

type ScenarioOption = { label: string; correct?: boolean; feedback?: string };
type Scenario = {
  prompt: string;
  framework?: string;
  options: ScenarioOption[];
  reveal?: string;
};

function extractScenarios(applications: unknown): Scenario[] {
  if (!applications) return [];
  if (Array.isArray(applications)) return applications as Scenario[];
  const a = applications as { scenarios?: Scenario[] };
  return Array.isArray(a.scenarios) ? a.scenarios : [];
}

function LessonScenarios({ applications, lessonId, authed }: {
  applications: unknown; lessonId: string; authed: boolean;
}) {
  const scenarios = extractScenarios(applications);
  const [picks, setPicks] = useState<Record<number, number>>({});
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  const [rule, setRule] = useState<{ required_indices: number[] | null; min_correct_attempts: number }>({
    required_indices: null, min_correct_attempts: 1,
  });
  const [ready, setReady] = useState(false);
  const recordMastery = useServerFn(recordScenarioMastery);
  const loadMastery = useServerFn(getLessonMastery);

  useEffect(() => {
    if (!authed || !scenarios.length) return;
    loadMastery({ data: { lessonId } })
      .then((r) => {
        setAttempts((r.attempts ?? {}) as Record<string, number>);
        setRule(r.rule ?? { required_indices: null, min_correct_attempts: 1 });
        setReady(Boolean(r.masteredAt));
      })
      .catch(() => { /* ignore */ });
  }, [authed, lessonId, scenarios.length, loadMastery]);

  if (!scenarios.length) return null;
  const total = scenarios.length;
  const countedIndices = (rule.required_indices ?? Array.from({ length: total }, (_, i) => i)).filter((i) => i < total);
  const minAttempts = rule.min_correct_attempts;
  const isCounted = (i: number) => countedIndices.includes(i);
  const satisfiedFor = (i: number) => (attempts[String(i)] ?? 0) >= minAttempts;
  const satisfiedCount = countedIndices.filter(satisfiedFor).length;
  const countedTotal = countedIndices.length;

  async function handlePick(si: number, oi: number, correct: boolean) {
    setPicks((p) => ({ ...p, [si]: oi }));
    if (!correct || !authed) return;
    try {
      const r = await recordMastery({ data: { lessonId, scenarioIndex: si, scenariosTotal: total } });
      setAttempts((r.attempts ?? {}) as Record<string, number>);
      if (r.rule) setRule(r.rule);
      if (r.mastered && !ready) {
        setReady(true);
        toast.success("Mastery achieved — you're ready");
      }
    } catch { /* silent */ }
  }

  return (
    <section className="mt-12 rounded-2xl border hairline p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-xl text-gold-luminous">Apply the framework</h2>
        <div className="flex items-center gap-2">
          {authed && countedTotal > 0 && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest ${
                ready ? "bg-gold/15 text-gold border border-gold/40"
                : "border hairline text-foreground/60"
              }`}
              aria-live="polite"
            >
              <Award size={11} /> {ready ? "Ready · mastered" : `Mastery ${satisfiedCount}/${countedTotal}`}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-widest text-foreground/50">
            Practice · instant feedback
          </span>
        </div>
      </div>
      <p className="mt-1 text-xs text-foreground/60">
        {countedTotal === 0
          ? "No scenarios are currently required for Ready. Ask your instructor to configure the mastery rule."
          : `Answer ${countedTotal === total ? "each counted scenario" : `the ${countedTotal} counted scenario${countedTotal === 1 ? "" : "s"}`} correctly ${minAttempts === 1 ? "at least once" : `${minAttempts} times`} to unlock the Ready badge.`}
      </p>

      <ol className="mt-5 space-y-8">
        {scenarios.map((s, si) => {
          const picked = picks[si];
          const pickedOpt = typeof picked === "number" ? s.options[picked] : undefined;
          const counted = isCounted(si);
          const scenarioMastered = counted && satisfiedFor(si);
          const correctCount = attempts[String(si)] ?? 0;
          return (
            <li key={si} className={`rounded-xl border p-4 ${scenarioMastered ? "border-gold/40 bg-gold/[0.04]" : "hairline bg-obsidian/40"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                {s.framework && (
                  <p className="text-[10px] uppercase tracking-widest text-gold">{s.framework}</p>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  {!counted && (
                    <span className="rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-widest text-foreground/50">
                      Practice · not counted
                    </span>
                  )}
                  {counted && authed && minAttempts > 1 && !scenarioMastered && (
                    <span className="rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-widest text-foreground/60">
                      {Math.min(correctCount, minAttempts)}/{minAttempts} correct
                    </span>
                  )}
                  {scenarioMastered && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                      <Check size={10} /> Mastered
                    </span>
                  )}
                </div>
              </div>
              <p className="mt-1 text-sm font-medium text-foreground/90">{si + 1}. {s.prompt}</p>
              <ul className="mt-3 space-y-2">
                {s.options.map((opt, oi) => {
                  const isPicked = picked === oi;
                  const showResult = typeof picked === "number";
                  const isCorrect = showResult && opt.correct;
                  const isWrongPick = showResult && isPicked && !opt.correct;
                  return (
                    <li key={oi}>
                      <button
                        type="button"
                        onClick={() => handlePick(si, oi, Boolean(opt.correct))}
                        className={`w-full min-h-11 rounded-lg border hairline px-3 py-2 text-left text-sm transition ${
                          isCorrect ? "border-emerald-500 bg-emerald-500/10"
                          : isWrongPick ? "border-red-500 bg-red-500/10"
                          : isPicked ? "border-gold"
                          : "hover:border-foreground/40"
                        }`}
                        aria-pressed={isPicked}
                      >
                        {opt.label}
                      </button>
                      {isPicked && opt.feedback && (
                        <p
                          role="status"
                          aria-live="polite"
                          className={`mt-2 rounded-md px-3 py-2 text-xs ${
                            opt.correct
                              ? "bg-emerald-500/10 text-emerald-200"
                              : "bg-amber-500/10 text-amber-200"
                          }`}
                        >
                          {opt.correct ? "✓ " : "→ "}{opt.feedback}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
              {typeof picked === "number" && s.reveal && pickedOpt?.correct && (
                <p className="mt-3 border-l-2 border-gold/50 pl-3 text-xs italic text-foreground/70">
                  {s.reveal}
                </p>
              )}
              {typeof picked === "number" && !scenarioMastered && (
                <button
                  type="button"
                  onClick={() => { const n = { ...picks }; delete n[si]; setPicks(n); }}
                  className="mt-3 rounded-full border hairline px-3 py-1 text-[11px] text-foreground/70"
                >
                  Try again
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {!authed && (
        <p className="mt-4 text-xs text-foreground/50">
          <Link to="/auth" className="underline decoration-gold/40 underline-offset-4 hover:text-gold">Sign in</Link> to track scenario mastery across your journey.
        </p>
      )}
    </section>
  );
}

/** Written works that share an author-written topic tag with this lesson. */
function RelatedBooks({ tags }: { tags: string[] | null }) {
  const matches = booksForTopics(tags, { limit: 3 });
  if (!matches.length) return null;
  return (
    <nav aria-label="Related written works" className="mt-12 rounded-2xl border hairline p-5">
      <h2 className="font-serif text-xl text-gold-luminous">Read further</h2>
      <ul className="mt-4 space-y-3 text-sm text-foreground/70">
        {matches.map(({ book, topics }) => (
          <li key={book.slug}>
            <Link
              to="/books/$slug"
              params={{ slug: book.slug }}
              className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
            >
              {book.title}
            </Link>
            <span> — {book.category}{topics.length ? ` · shares the topic of ${topicLabel(topics)}` : ""}</span>
          </li>
        ))}
      </ul>
    </nav>
  );
}
